import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Typography,
  IconButton,
  Stack,
  Tooltip,
  Divider,
  Button,
  Box,
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useAtomValue } from 'jotai';
import useAPI from '../../../api';
import PeriodPicker from '../../../Components/Inputs/DbeDatePicker';
import MultipleChipSelect from '../../../Components/Inputs/MultipleChipSelect';
import CheckboxStack from '../../../Components/Inputs/CheckboxStack';
import FetchAutoComplete from '../../../Components/Inputs/FetchAutoComplete';
import { outcomesAtom } from '../../../Atoms';

const useStyles = () => ({
  root: {
    padding: '0px 8px',
    width: '25vw',
  },
});

/**
 * Filters menu child for WavePeriod filter selection:
 *
 * @param {Object} props - Component props.
 * @param {String} props.wavePeriod - String. Selected wave period string filter; ES: 202311 is November 23.
 * @param {Function} props.onConfirmPeriod - Callback. Function executed on confirm click that sets wavePeriod filter with a new value.
 * @param {Function} props.onClose - Function. Executed on Filters menu closure.
 *
 * @returns {React.JSX.Element}
 */
/* eslint-disable react/prop-types */
const ActionBarCustom = function ActionBarCustom({ onClick }) {
  const intl = useIntl();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        position: 'sticky',
        bottom: 0,
      }}
    >
      <Button id="confirm-spot-filters" onClick={onClick}>
        {intl.messages.CONFIRM}
      </Button>
    </Box>
  );
};

/* const getDefaultPeriod = (defaultValue) => {
  if (defaultValue) {
    return new Date(
      `${defaultValue.substring(0, 4)}-${defaultValue.substring(4)}`
    ); //adds a - to have a valid date ES: 2023-11
  }
  return new Date();
};
 */
const CustomSpotFilters = function CustomSpotFilters({
  onConfirmPeriod,
  wavePeriod = '',
  onClose = () => {},
}) {
  const intl = useIntl();
  const classes = useStyles();
  const API = useAPI();

  const sectionOutcomes = useAtomValue(outcomesAtom);

  const [offer, setOffer] = useState(
    JSON.parse(localStorage.getItem('tag')) || ''
  );
  const [selectedCallFilter, setSelectedCallFilter] = useState(
    JSON.parse(localStorage.getItem('lastCallInCurrentMonth'))
  );
  // const [period, setPeriod] = useState(getDefaultPeriod(wavePeriod));
  const [outcomes, setOutcomes] = useState(
    localStorage.getItem('lastCallOutcome') &&
      localStorage.getItem('lastCallOutcome') !== 'undefined'
      ? JSON.parse(localStorage.getItem('lastCallOutcome'))?.map((value) =>
          sectionOutcomes.find(({ id }) => id === value)?.name?.toUpperCase()
        )
      : null
  );
  const [onlyCurrentWave, setOnlyCurrentWave] = useState(
    localStorage.getItem('lastCallOutcome') &&
      JSON.parse(localStorage.getItem('calledInCurrentPeriod'))
      ? [0]
      : [null]
  );

  const handleOutcomesChange = (newValues) => {
    if (!Array.isArray(newValues) || newValues.length === 0) setOutcomes(null);
    else {
      setOutcomes(newValues);
    }
  };

  const handleChangeOnlyCurrentWave = (newValue) => {
    setOnlyCurrentWave(newValue);
  };

  const selectCallFilter = (value) => {
    setSelectedCallFilter(value);
  };

  const handleChangeOffer = (e, newValue) => {
    setOffer(newValue);
  };

  const handleAcceptPeriod = () => {
    onConfirmPeriod(
      selectedCallFilter,
      outcomes
        ? outcomes?.map(
            (o) =>
              sectionOutcomes.find(
                ({ name }) => name.toUpperCase() === o.toUpperCase()
              )?.id
          )
        : null,
      outcomes?.length > 0 &&
        onlyCurrentWave.length > 0 &&
        onlyCurrentWave[0] !== null,
      offer
    );
    onClose();
  };

  const getOfferOptions = (searchValue, onSuccess, onFailure) =>
    API.getOfferOptions('Spot', searchValue, onSuccess, onFailure);

  return (
    <div style={classes.root}>
      <Typography
        variant="overline"
        sx={{ mb: '8px', pl: '24px', opacity: '60%' }}
      >
        {intl.messages.MONTH_CALLED_INDICATOR}
      </Typography>
      <Stack
        id="user-status-btns"
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'space-around',
          mb: '16px',
        }}
      >
        <Tooltip title={intl.messages.SHOW_CUSTOMERS_ALREADY_CALLED}>
          <IconButton size="large" onClick={() => selectCallFilter(1)}>
            <CallIcon
              color={selectedCallFilter === 1 ? 'success' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.messages.SHOW_CUSTOMERS_NOT_CALLED}>
          <IconButton size="large" onClick={() => selectCallFilter(0)}>
            <PhoneDisabledIcon
              color={selectedCallFilter === 0 ? 'error' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.messages.SHOW_ALL}>
          <IconButton size="large" onClick={() => selectCallFilter(null)}>
            <FilterAltOffIcon
              color={selectedCallFilter === null ? 'primary' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <div style={{ padding: '16px' }}>
        <MultipleChipSelect
          label={intl.messages.LAST_CALL_OUTCOME}
          onChange={handleOutcomesChange}
          values={outcomes || []}
          options={sectionOutcomes?.map(({ name }) => name?.toUpperCase())}
        />
        <CheckboxStack
          data={[
            {
              label: intl.messages.CURRENT_WAVE_OUTCOMES,
              value: 0,
              disabled: !outcomes || outcomes?.length < 1,
            },
          ]}
          defaultCheckedAr={onlyCurrentWave}
          handleChange={handleChangeOnlyCurrentWave}
        />
      </div>
      <div style={{ padding: '16px' }}>
        <FetchAutoComplete
          id="offer-automplete"
          label={intl.messages.OFFER}
          width="100%"
          selectedValue={offer}
          onChangeSelectedValue={handleChangeOffer}
          fetchCallback={getOfferOptions}
          noCustomValue
        />
      </div>
      {/* <PeriodPicker
        disableFuture
        staticPicker
        date={period}
        onChange={(newDate) => {
          setPeriod(newDate);
        }}
        onClose={onClose}
        localeText={{ datePickerToolbarTitle: intl.messages.PERIOD }}
        views={['month', 'year']}
        orientation="portrait"
        slotProps={{
          layout: {
            sx: { display: 'flex', flexDirection: 'column' },
          },
          actionBar: { onClick: handleAcceptPeriod },
        }}
        slots={{
          actionBar: ActionBarCustom,
        }}
      /> */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <Button id="confirm-spot-filters" onClick={handleAcceptPeriod}>
          {intl.messages.CONFIRM}
        </Button>
      </Box>
    </div>
  );
};

CustomSpotFilters.propTypes = {
  wavePeriod: PropTypes.string,
  onClose: PropTypes.func,
  onConfirmPeriod: PropTypes.func.isRequired,
};

export default CustomSpotFilters;
